import {IBytecode} from "../interfaces/IBytecode";
import {ITimedStop} from "../interfaces/ITimedStop";
import {IWithMetaPoint} from "../interfaces/IWithMetaPoint";
import {LegHandler} from "./LegHandler";
import {ILegEvaluation} from "../interfaces/ILegEvaluation";
import {IEnginePointSelection} from "../interfaces/IEnginePointSelection";
import {Geodesic} from "..";
import moment from "moment-timezone";
import {IPoint} from "../interfaces/IPoint";
import {IProjectedTimedStop} from "../interfaces/IProjectedTimedStop";
import {IDirection} from "../interfaces/IDirection";
import {IProjectedDirection} from "../interfaces/IProjectedDirection";

const OUT_OF_TRACK_RADIUS: number = 20;  //OUT_OF_TRACK_RADIUS represents a too big gap
const QUICK_RESULT_MAX_RADIUS: number = 5;  //20 meters and you're away from the track.
const STOP_STICKYNESS: number = 20;  //20 meters and you're not at the stop


export class Engine {

    private readonly bytecode: IBytecode;
    private projectedTimedStops: IProjectedTimedStop[] = [];
    private projectedDirections: IProjectedDirection[] = [];


    private legHandlers: LegHandler[] = [];
    private physicsEnabled: Boolean = false;    //Check if physics is enabled or not
    private referenceDay: string|null = null;               //The day of the current trip, like 2021-01-15. Required to compute lateness since points can be after midnight.
    //private performance: Boolean = false;       //Check if performance must be computed while projecting

    /**
     * Bytecode : a bytecode created by the bytecode builder
     * timedStops : the timed stops that are matching the bytecode.
     * @param bytecode
     * @param timedStops
     * @param refPointList
     * @param directions
     */
    constructor(bytecode: IBytecode, timedStops: ITimedStop[], refPointList: IPoint[], directions:IDirection[]) {
        if(directions === null || directions === undefined) {
            directions = [];
        }
        this.bytecode = bytecode;
        for(let i = 0; i < bytecode.legs.length; i++) {
            this.legHandlers.push(new LegHandler(this.bytecode, timedStops, i, refPointList, directions));
        }
        for(let i = 0; i < this.legHandlers.length; i++) {
            this.projectedTimedStops.push(...this.legHandlers[i].getSelfStops());
            this.projectedDirections.push(...this.legHandlers[i].getSelfDirections());
        }
    }

    enablePhysics() {
        this.physicsEnabled = true;
    }

    // enablePerformanceCheck() {
    //     this.performance = true;
    // }
    // disablePerformanceCheck() {
    //     this.performance = false;
    // }
    disablePhysics() {
        this.physicsEnabled = false;
    }

    enableLatenessComputation(referenceDay: string) {
        this.referenceDay = referenceDay;
    }


    project(iWithMetaPoint: IWithMetaPoint) : IEnginePointSelection{

        let result = this.findCorrectLegEvaluation(iWithMetaPoint);
        if(this.physicsEnabled && result.respectsPhysics) {
            this.lastValidEnginePointSelection = result;

        }

        result.currentStop =
            this.legHandlers[result.selectedLeg]
                .getSelfStops()
                .filter(e => {
                    return Math.abs(e.distanceFromOrigin - result.legEvaluation.distance) < STOP_STICKYNESS
                })
                .find(_ => true);   //We take the first one only.
        result.nextStop =
            this.projectedTimedStops
                .filter(e => e.distanceFromOrigin > result.legEvaluation.distance)
                .filter(e => {if(result.currentStop !== null && result.currentStop !== undefined) { return e.id !== result.currentStop.id } return true})   //Removing the current stop from the candidates.
                .sort((a,b) => a.distanceFromOrigin - b.distanceFromOrigin)
                .find(_ => true);   //We Take the first one only

        result.nextDirection =
            this.projectedDirections
                .filter(e => e.distanceFromOrigin > result.legEvaluation.distance)
                .sort((a,b) => a.distanceFromOrigin - b.distanceFromOrigin)
                .find(_ => true);   //We Take the first one only

        if(this.referenceDay && iWithMetaPoint.dateTime !== null && result.currentStop != undefined) {
            result.lateness = this.evaluateLateness(iWithMetaPoint.dateTime, result.currentStop);

        }
        return result;
    }

    /**
     * This function should be called at a point where we are only.
     * @param dateTimeISO
     * @param stop
     * Positive value: you're late, negative value : you're too soon.
     * Computed in seconds.
     */
    evaluateLateness(dateTimeISO: string, stop: IProjectedTimedStop) {
        if(this.referenceDay) {
            let whenPoint = moment(dateTimeISO);
            let minDT = moment(this.referenceDay);
            let maxDT = moment(this.referenceDay);
            minDT.add(stop.arrivalTime.split(":")[0], "hours");
            minDT.add(stop.arrivalTime.split(":")[1], "minutes");
            minDT.add(stop.arrivalTime.split(":")[2], "seconds");
            maxDT.add(stop.departureTime.split(":")[0], "hours");
            maxDT.add(stop.departureTime.split(":")[1], "minutes");
            maxDT.add(stop.departureTime.split(":")[2], "seconds");
            //You're late if whenPoin
            if(whenPoint.diff(minDT, "seconds")  < 0) {
                //whenPoint is before arrival time
                return whenPoint.diff(minDT, "seconds");
            } else if(whenPoint.diff(maxDT, "seconds") > 0){
                //whenPoint is after departure time
                return whenPoint.diff(maxDT, "seconds");
            }

            //WhenPoint is contained between arrival & departure time.
            return 0;
        }
        return 0;
    }

    /**
     * @param wmPoint
     */
    findCorrectLegEvaluation(wmPoint: IWithMetaPoint) : IEnginePointSelection {
        /**
         * Quick result : If we already were on a leg, we will first try to project only on this leg and check if the
         * retrived data is correct
         */
        if(this.lastMatchingLeg !== -1) {
            let lh = this.legHandlers[this.lastMatchingLeg];
            let quickResult = lh.evaluatePoint(wmPoint);
            if(quickResult.gap < QUICK_RESULT_MAX_RADIUS) {
                //The quick result is mostly good, (low gap) but can be wrong if we're on another leg.
                if(!((quickResult.indexOnShape === lh.firstIndex || quickResult.indexOnShape === lh.lastIndex) && quickResult.placement === "IN_PLACE")) {
                    if(!(this.physicsEnabled && !this.respectsPhysics(quickResult, wmPoint))) {
                        //If physics is enabled, we need to check if this point respects it, otherwise we don't care.
                    }
                    return {
                        attempts: 1,
                        quicklySelected: true,
                        sourcePoint: wmPoint,
                        tooFarAway: false,
                        respectsPhysics: true,
                        selectedLeg: this.lastMatchingLeg,
                        legEvaluation: quickResult,
                        computationSpeed: 0,
                        currentStop: undefined,
                        nextStop: undefined,
                        nextDirection: undefined,
                        lateness: undefined
                    };
                }
            }
            this.lastMatchingLeg = -1;   //If we reached this portion of the code, then we have no matching leg.

        }

        //Retrieving the results for all legs for a global computation
        let results: ILegEvaluation[] = [];

        for(let legHandler of this.legHandlers) {
            results.push(legHandler.evaluatePoint(wmPoint));
        }

        results =  results.sort((a:ILegEvaluation, b:ILegEvaluation) => {
            return a.gap - b.gap;
        }); //Sorting result by gap distance between the point and it's projection.


        let workingResults = results.slice();
        let selectedResult: ILegEvaluation = workingResults[0]; //Default value

        let attempts  = 0;

        let bestWithoutPhysics = function(results: ILegEvaluation[]): ILegEvaluation {
            let result;
            if(results.length > 1) {
                if(results[0].placement === "IN_PLACE" && results[1].placement !== "IN_PLACE") {
                    //Means that we're certainly at the bound of a leg.
                    result = workingResults[1]; //Works very well.
                } else {
                    result = workingResults[0];
                }
            } else {
                result = workingResults[0];
            }
            return result;
        }

        let respectsPhysics = true;
        if(!this.physicsEnabled) {
            selectedResult = bestWithoutPhysics(workingResults);
        } else {

            let compliantResults = workingResults.filter(e => e.gap < OUT_OF_TRACK_RADIUS);
            if(compliantResults.length >= 1) {
                if(compliantResults.length === 1) {
                    // console.log("Only one result respects radius constraint");
                                                                                                                        //There's only a single result that match.
                    selectedResult = compliantResults[0];
                } else {
                    // console.log("We have several compliant results");
                                                                                                                        //We have several results.
                    if(this.lastValidEnginePointSelection !== null) {
                        let lastDist = this.lastValidEnginePointSelection.legEvaluation.distance;
                        compliantResults.sort((a:ILegEvaluation, b:ILegEvaluation) => {
                            return Math.abs(a.distance - lastDist) - Math.abs(b.distance - lastDist)
                        });                                                                                             //Compliant results are sorted by distance with the last valid selected point
                        // console.log(compliantResults);
                                                                                                                        //We take the first result that could be reached at a speed contained between -10km/h and 110km/h
                        let milliseconds = moment(wmPoint.dateTime).diff(moment(this.lastValidEnginePointSelection.sourcePoint.dateTime), 'millisecond');
                        // console.log("time between last points : " + milliseconds);
                        for(let result of compliantResults) {
                            let speed = (result.distance - lastDist)/(milliseconds/1000);
                            // console.log("Computed speed : " + (speed*3.6) + " km/h for leg " + result.leg);
                            if(speed >= -30/3.6 && speed <= 130/3.6) {
                                selectedResult = result;
                                break;
                            }
                        }
                    } else {
                        // console.log("We have no previous compliant result");
                                                                                                                        //We have no clue, the best result should be the one with the least distance on track.
                        selectedResult = compliantResults.sort((a:ILegEvaluation, b:ILegEvaluation) => { return a.distance - b.distance })[0];
                    }
                }

            } else {
                // console.log("No result respects the out of radius constraint", workingResults);
                respectsPhysics = false;
                selectedResult = bestWithoutPhysics(workingResults);
            }
        }

        return {
            attempts: attempts,
            quicklySelected: false,
            sourcePoint: wmPoint,
            tooFarAway: selectedResult.gap > OUT_OF_TRACK_RADIUS,
            respectsPhysics: respectsPhysics,
            selectedLeg: selectedResult.leg,
            legEvaluation: selectedResult,
            computationSpeed: 0,
            currentStop: undefined,
            nextStop: undefined,
            nextDirection: undefined,
            lateness: undefined
        }
    }


    /**
     * User send us timed points
     *   - We compute the distance between the last two timed points and also the time
     *   - We then now how much distance the user made between those two points
     *   - The distance between the two legs evaluation (of the previous point and of this point) must be close to the
     *   distance that the user made between the two initial positions.
     *
     * @todo: change implementation to do this.
     *
     * @param legEvaluation
     * @param wmPoint
     */
    respectsPhysics(legEvaluation:ILegEvaluation, wmPoint: IWithMetaPoint) {
        if(this.lastValidEnginePointSelection === null) {
            return true;
        } else {

            let seconds = moment(wmPoint.dateTime).diff(moment(this.lastValidEnginePointSelection.sourcePoint.dateTime), 'seconds');

            if(seconds < 4)  {
                return true; // If seconds < 4 : we cannot state this (This is designed for a 1s refresh time between two positions)
            }

            let realMeters = Geodesic.metersBetween(this.lastValidEnginePointSelection.sourcePoint, wmPoint);
            let projectedMeters = legEvaluation.distance - this.lastValidEnginePointSelection.legEvaluation.distance;


            if(projectedMeters < -2) {
                // We cannot allow going backwards on track. (-2 meters is a tolerance for projection hazard on a standing position)
                return false;

            }

            //Let's allow a maximum of 50%  times the distance on the projected meters.
            if(projectedMeters <= realMeters * 2) {
                return true;
            }
            return false;


        }
    }



    lastMatchingLeg: number = -1; //The index of the leg that retrieved the good data.
    lastValidEnginePointSelection: IEnginePointSelection|null = null;   //The last valid point (depends if physics & out of track detection are enabled or not)
    lastEnginePointSelection:IEnginePointSelection|null = null;         //The last computed point.







}
